/**
 * Fruit Grab — kid-friendly reaction race.
 *
 * Rules
 *   - A plate sits on a table, covering a hidden fruit.
 *   - After a random suspense the plate lifts and the fruit is revealed.
 *   - First player to tap the screen (or press Space) grabs the fruit.
 *   - Good fruits = +1 point, bad fruits = -1 point (floored at 0).
 *   - First player to reach 6 wins the match.
 *
 * Architecture
 *   - synchronized mode — the host owns the whole state machine and
 *     broadcasts snapshots every ~15 Hz. Clients apply snapshots and
 *     render + animate from them. Scene `update()` drives Phaser-level
 *     rendering on every frame on host + clients.
 *   - first-press-wins — on the host tick, a rising edge on any player's
 *     `fire` input after REVEAL started wins the fruit. We capture each
 *     player's fire state at the moment REVEAL begins as a "baseline" so
 *     a player who was already holding the button cannot auto-grab.
 *     Ties inside a single tick are broken by sorted player id.
 *   - scoring uses the SDK `Leaderboard` helper so score deltas are
 *     broadcast and mirrored on every peer for the HUD.
 *   - zero external sprite dependencies: the plate / table / particles
 *     are drawn with Phaser Graphics, fruits are Unicode emoji rendered
 *     as Text so every platform gets a recognisable icon without asset
 *     bloat.
 */
import { SdkGameScene, Leaderboard } from '@platform/sdk';

// ─────────────────────────────────────────────────────────── constants

const TARGET_SCORE = 6;

// Phase timings (ms)
const COUNTDOWN_MS = 3200;
const WAIT_MIN_MS  = 700;
const WAIT_MAX_MS  = 1900;
const LIFT_MS      = 520;
const REVEAL_MS    = 3500;
const GRABBED_MS   = 1500;
const MISSED_MS    = 1000;
const END_MS       = 4800;

// Stage layout — normalized coords applied at runtime via scale.width/height.
// The cover is drawn as a silver cloche dome whose RIM rests on the table.
const PLATE_X         = 0.5;
const PLATE_Y         = 0.78;        // y of the table top / cover rim at rest
const COVER_W_FACTOR  = 0.40;        // dome width as a fraction of stage width
const COVER_HEIGHT_PX = 200;         // dome height (rim → top of dome)
const PLATE_LIFT_PX   = 230;
const FRUIT_FONT_PX   = 84;

// Fruit catalogue
const GOOD_FRUITS = [
  { id: 'apple',      emoji: '🍎', color: 0xef4444, delta: +1, name: 'apple'      },
  { id: 'orange',     emoji: '🍊', color: 0xfb923c, delta: +1, name: 'orange'     },
  { id: 'banana',     emoji: '🍌', color: 0xfacc15, delta: +1, name: 'banana'     },
  { id: 'strawberry', emoji: '🍓', color: 0xf43f5e, delta: +1, name: 'strawberry' },
  { id: 'watermelon', emoji: '🍉', color: 0x16a34a, delta: +1, name: 'watermelon' },
  { id: 'grapes',     emoji: '🍇', color: 0x8b5cf6, delta: +1, name: 'grapes'     },
  { id: 'pineapple',  emoji: '🍍', color: 0xeab308, delta: +1, name: 'pineapple'  },
  { id: 'peach',      emoji: '🍑', color: 0xfb7185, delta: +1, name: 'peach'      }
];
const BAD_FRUITS = [
  { id: 'skull', emoji: '💀', color: 0x9ca3af, delta: -1, name: 'spooky' },
  { id: 'chili', emoji: '🌶️', color: 0xdc2626, delta: -1, name: 'spicy'  },
  { id: 'slime', emoji: '🦠', color: 0x65a30d, delta: -1, name: 'yuck'   },
  { id: 'poop',  emoji: '💩', color: 0x78350f, delta: -1, name: 'ewww'   }
];
const BAD_FRUIT_CHANCE = 0.33;

// Per-player highlight colours for the HUD
const PLAYER_COLORS = [
  0xef4444, 0x3b82f6, 0x10b981, 0xf59e0b,
  0xec4899, 0x8b5cf6, 0x06b6d4, 0xf97316
];

// Phase enum
const P_COUNTDOWN = 'countdown';
const P_WAIT      = 'wait';
const P_LIFT      = 'lift';
const P_REVEAL    = 'reveal';
const P_GRABBED   = 'grabbed';
const P_MISSED    = 'missed';
const P_END       = 'end';

// HUD layout — per-row dot positions are computed inside _buildHudRow so
// rows in narrow (two-column) layouts can shrink the spacing.
const HUD_ROW_HEIGHT = 44;
const HUD_ROW_TOP    = 14;
const HUD_ROW_PILL_Y = 18;

// ─────────────────────────────────────────────────────────── scene

export default class GameScene extends SdkGameScene {

  start({ players }) {
    this.W = this.scale.width;
    this.H = this.scale.height;

    // Build player lookup tables (id → name, id → color) from the roster.
    this._players = (players || this.sdk.players || []).slice();
    this._playerNames = {};
    this._playerColors = {};
    for (let i = 0; i < this._players.length; i++) {
      const p = this._players[i];
      this._playerNames[p.id] = p.name || p.id;
      this._playerColors[p.id] = PLAYER_COLORS[i % PLAYER_COLORS.length];
    }

    // Authoritative state. Host writes it from tick(); clients overwrite
    // it via applySnapshot().
    this.state = {
      phase: P_COUNTDOWN,
      phaseAt: 0,
      phaseLen: COUNTDOWN_MS,
      round: 0,
      fruit: null,
      winnerId: null,
      finalWinnerId: null,
      matchOver: false
    };

    // Host-only: queue of grab attempts received this tick. Each entry is
    // a { playerId, ts } object. Drained on every tick during P_REVEAL.
    this._grabQueue = [];

    // Subscribe to grab events from clients (host) AND the host's own local
    // press (host pushes directly via _fireLocalGrab). The custom event bus
    // is more reliable than tunneling presses through the input/serialize
    // pipeline because it doesn't depend on tick timing of the virtual
    // button latch.
    if (this.sdk.isHost) {
      this._unsubGrab = this.sdk.on('fg-grab', (payload) => {
        if (payload && payload.playerId) {
          this._grabQueue.push({ playerId: payload.playerId, ts: payload.ts || Date.now() });
        }
      });
    }

    // "Wrong click" feedback runs on every peer when the host broadcasts
    // a fg-wrong event (i.e. somebody pressed too early during WAIT/LIFT).
    this._unsubWrong = this.sdk.on('fg-wrong', (payload) => {
      this._onWrongClickFx(payload && payload.playerId);
    });

    // Leaderboard drives per-player score tracking + HUD refresh.
    this.lb = new Leaderboard({ sdk: this.sdk });
    this.lb.onUpdate(({ scores }) => this._renderHudScores(scores));

    // Cache current dimensions — used by onResize() to decide whether
    // to rebuild the HUD.
    this._lastResizeW = this.W;
    this._lastResizeH = this.H;

    // Scene construction.
    this._buildBackground();
    this._buildTable();
    this._buildPlateAndFruit();
    this._buildParticleLayers();
    this._buildHud();
    this._buildOverlayText();
    this._renderHudScores(this.lb.getAll());

    this._setupLocalInput();
    this._startMusic();

    // Cleanup on shutdown.
    this.events.once('shutdown', () => this._teardown());
  }

  // ─────────────────────────────────────────────────── host simulation

  tick(dt, _inputs) {
    if (!this.sdk.isHost) return;
    if (this.state.matchOver) return;

    this.state.phaseAt += dt * 1000;

    // Taps land in three buckets depending on the current phase:
    //   - REVEAL                : the first one wins this round (handled below)
    //   - WAIT or LIFT          : early — penalize -1, dedupe per player per tick
    //   - everything else       : ignored, queue cleared
    if (this.state.phase === P_WAIT || this.state.phase === P_LIFT) {
      const seen = new Set();
      while (this._grabQueue.length) {
        const wrong = this._grabQueue.shift();
        if (seen.has(wrong.playerId)) continue;
        seen.add(wrong.playerId);
        this._onWrongClick(wrong.playerId);
      }
    } else if (this.state.phase !== P_REVEAL) {
      this._grabQueue.length = 0;
    }

    switch (this.state.phase) {
      case P_COUNTDOWN:
        if (this.state.phaseAt >= this.state.phaseLen) this._beginRound();
        break;

      case P_WAIT:
        if (this.state.phaseAt >= this.state.phaseLen) this._enterPhase(P_LIFT, LIFT_MS);
        break;

      case P_LIFT:
        if (this.state.phaseAt >= this.state.phaseLen) this._enterPhase(P_REVEAL, REVEAL_MS);
        break;

      case P_REVEAL: {
        // First grab in the queue wins. Order of arrival = order of reaction.
        if (this._grabQueue.length) {
          const first = this._grabQueue.shift();
          this._grabQueue.length = 0;
          this._onGrab(first.playerId);
        } else if (this.state.phaseAt >= this.state.phaseLen) {
          this._enterPhase(P_MISSED, MISSED_MS);
        }
        break;
      }

      case P_GRABBED:
      case P_MISSED:
        if (this.state.phaseAt >= this.state.phaseLen) this._nextRoundOrEnd();
        break;

      case P_END:
        if (this.state.phaseAt >= this.state.phaseLen) this._finalizeMatch();
        break;
    }
  }

  _enterPhase(phase, lenMs) {
    const prev = this.state.phase;
    this.state.phase = phase;
    this.state.phaseAt = 0;
    this.state.phaseLen = lenMs;
    this._onPhaseChanged(prev, phase);
  }

  _beginRound() {
    this.state.round++;
    const good = this.sdk.rng.next() >= BAD_FRUIT_CHANCE;
    const table = good ? GOOD_FRUITS : BAD_FRUITS;
    const f = this.sdk.rng.pick(table);
    this.state.fruit = {
      id: f.id,
      emoji: f.emoji,
      color: f.color,
      delta: f.delta,
      name: f.name,
      good: good,
      rotation: this.sdk.rng.floatBetween(-0.25, 0.25)
    };
    this.state.winnerId = null;
    const waitMs = WAIT_MIN_MS + this.sdk.rng.floatBetween(0, WAIT_MAX_MS - WAIT_MIN_MS);
    this._enterPhase(P_WAIT, waitMs);
  }

  _onWrongClick(playerId) {
    if (!playerId) return;
    // Decrement the offending player by 1 (clamped at 0).
    const cur = this.lb.get(playerId);
    if (cur > 0) this.lb.report({ playerId, delta: -1 });
    // Broadcast feedback to every peer (including this host).
    this.sdk.emit('fg-wrong', { playerId });
  }

  _onGrab(playerId) {
    const fruit = this.state.fruit;
    if (!fruit) return;

    // Clamp score at 0 so young players don't wind up in the negatives.
    const cur = this.lb.get(playerId);
    let delta = fruit.delta;
    if (cur + delta < 0) delta = -cur;

    this.state.winnerId = playerId;
    if (delta !== 0) this.lb.report({ playerId, delta });

    if (this.lb.get(playerId) >= TARGET_SCORE) {
      this.state.finalWinnerId = playerId;
    }

    this._enterPhase(P_GRABBED, GRABBED_MS);
  }

  _nextRoundOrEnd() {
    if (this.state.finalWinnerId) {
      this._enterPhase(P_END, END_MS);
      return;
    }
    this._beginRound();
  }

  _finalizeMatch() {
    if (this.state.matchOver) return;
    this.state.matchOver = true;
    const winner = this.lb.getWinner();
    const scores = {};
    for (const r of this.lb.getAll()) scores[r.id] = r.score;
    this.sdk.endMatch({
      winnerName: winner ? winner.name : 'tie',
      scores
    });
  }

  // ─────────────────────────────────────────────────── snapshots

  snapshot() {
    return {
      phase: this.state.phase,
      phaseAt: this.state.phaseAt,
      phaseLen: this.state.phaseLen,
      round: this.state.round,
      fruit: this.state.fruit,
      winnerId: this.state.winnerId,
      finalWinnerId: this.state.finalWinnerId,
      matchOver: this.state.matchOver
    };
  }

  applySnapshot(snap) {
    if (!snap) return;
    const prev = this.state.phase;
    this.state.phase = snap.phase;
    this.state.phaseAt = snap.phaseAt;
    this.state.phaseLen = snap.phaseLen;
    this.state.round = snap.round;
    this.state.fruit = snap.fruit;
    this.state.winnerId = snap.winnerId;
    this.state.finalWinnerId = snap.finalWinnerId;
    this.state.matchOver = snap.matchOver;
    if (prev !== snap.phase) this._onPhaseChanged(prev, snap.phase);
  }

  _onPhaseChanged(prev, next) {
    // Fire visual + audio effects on phase transitions. Runs on the host
    // (via _enterPhase) AND on clients (via applySnapshot) so each peer
    // plays its own local SFX and spawns its own particles.
    if (next === P_LIFT) {
      this._playSfx('fg_whoosh');
    } else if (next === P_REVEAL) {
      this._spawnRevealBurst(this.state.fruit);
      this.cameras.main.shake(140, 0.005);
    } else if (next === P_GRABBED) {
      const fruit = this.state.fruit;
      if (fruit && fruit.good) {
        this._playSfx('fg_good');
        this._spawnConfetti();
      } else {
        this._playSfx('fg_bad');
        this._spawnPoof();
        this.cameras.main.shake(220, 0.01);
      }
    } else if (next === P_MISSED) {
      this._playSfx('fg_miss');
    } else if (next === P_END) {
      this._playSfx('fg_win');
      this._spawnFireworks();
    }
  }

  _playSfx(key) {
    if (this.sdk.audio && this.sdk.audio.playSfx) {
      try { this.sdk.audio.playSfx(key); } catch (_) { /* ignore */ }
    }
  }

  _onWrongClickFx(playerId) {
    this._playSfx('fg_bad');
    this.cameras.main.shake(180, 0.008);
    this._spawnPoof();
    // Pulse the offending player's HUD row red.
    if (playerId && this.hudRows && this.hudRows[playerId]) {
      const row = this.hudRows[playerId];
      this.tweens.killTweensOf(row.group);
      this.tweens.add({
        targets: row.group,
        scale: { from: 0.85, to: 1 },
        duration: 260,
        ease: 'Back.out'
      });
    }
  }

  // ─────────────────────────────────────────── scene construction

  _buildBackground() {
    const W = this.W, H = this.H;
    const g = this.add.graphics().setDepth(0);

    // Sky gradient approximated with stacked bands.
    const stops = [
      { y: 0.00, color: 0xfff4cc },
      { y: 0.40, color: 0xffd59e },
      { y: 0.78, color: 0xffc18a }
    ];
    for (let i = 0; i < stops.length - 1; i++) {
      g.fillStyle(stops[i].color, 1);
      g.fillRect(0, stops[i].y * H, W, (stops[i + 1].y - stops[i].y) * H + 2);
    }
    g.fillStyle(stops[stops.length - 1].color, 1);
    g.fillRect(0, stops[stops.length - 1].y * H, W, H - stops[stops.length - 1].y * H);

    // Big sun halo behind the scene.
    g.fillStyle(0xffffff, 0.22);
    g.fillCircle(W * 0.82, H * 0.22, 150);
    g.fillStyle(0xffffff, 0.35);
    g.fillCircle(W * 0.82, H * 0.22, 90);
    g.fillStyle(0xffffff, 0.6);
    g.fillCircle(W * 0.82, H * 0.22, 52);

    // Fluffy bokeh dots.
    for (let i = 0; i < 18; i++) {
      const x = (i * 97.3 + 40) % W;
      const y = ((i * 53.7) % (H * 0.45)) + 40;
      const r = 10 + (i % 4) * 5;
      g.fillStyle(0xffffff, 0.14);
      g.fillCircle(x, y, r + 6);
      g.fillStyle(0xffffff, 0.22);
      g.fillCircle(x, y, r);
    }
  }

  _buildTable() {
    const W = this.W, H = this.H;
    const g = this.add.graphics().setDepth(1);

    const clothTopY = H * PLATE_Y;

    // Wood rim peeking out above the tablecloth.
    g.fillStyle(0x92400e, 1);
    g.fillRect(0, clothTopY - 10, W, 10);
    g.fillStyle(0xb45309, 1);
    g.fillRect(0, clothTopY - 10, W, 4);

    // Cream tablecloth.
    g.fillStyle(0xffedd5, 1);
    g.fillRect(0, clothTopY, W, H - clothTopY);

    // Darker underside shading at the very bottom (perspective).
    g.fillStyle(0xfed7aa, 1);
    g.fillRect(0, H - 28, W, 28);

    // Pink polka dots on the tablecloth.
    const rows = Math.max(2, Math.floor((H - clothTopY) / 36));
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < 12; col++) {
        const x = (col + (row % 2 ? 0.5 : 0)) * (W / 11);
        const y = clothTopY + 22 + row * 36;
        g.fillStyle(0xfca5a5, 0.75);
        g.fillCircle(x, y, 7);
        g.fillStyle(0xfff1f2, 0.6);
        g.fillCircle(x - 2, y - 2, 3);
      }
    }
  }

  _buildPlateAndFruit() {
    const W = this.W, H = this.H;
    const px = W * PLATE_X;
    const py = H * PLATE_Y;          // table top y / cover rim resting y
    const cw = W * COVER_W_FACTOR;   // dome width
    const ch = COVER_HEIGHT_PX;      // dome height (rim → top)

    // Soft cast shadow on the table where the cover sits.
    this.plateShadow = this.add.graphics().setDepth(2);
    this.plateShadow.fillStyle(0x78350f, 0.42);
    this.plateShadow.fillEllipse(0, 0, cw * 1.18, 30);
    this.plateShadow.x = px;
    this.plateShadow.y = py + 14;

    // Halo drawn underneath the fruit emoji.
    this.fruitHalo = this.add.graphics().setDepth(3);

    // Fruit emoji — rendered as Text so every platform shows colour.
    // It sits ON the table: its bottom edge meets the table line.
    const fruitY = py - FRUIT_FONT_PX * 0.5;
    this.fruitText = this.add.text(px, fruitY, '', {
      fontFamily: 'system-ui, "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif',
      fontSize: FRUIT_FONT_PX + 'px',
      color: '#ffffff'
    }).setOrigin(0.5).setDepth(4);
    this._fruitRestY = fruitY;

    // The cloche cover — a container we translate as a single unit. Inside
    // the container, (0,0) is the rim center; the dome is drawn upward
    // from there.
    this.plate = this.add.container(px, py).setDepth(5);
    const pg = this.add.graphics();
    const rx = cw / 2;

    // Base ring (where the cover meets the table).
    pg.fillStyle(0x6b7280, 1);
    pg.fillEllipse(0, 0, cw * 1.05, 22);
    pg.lineStyle(2, 0x4b5563, 1);
    pg.strokeEllipse(0, 0, cw * 1.05, 22);
    pg.fillStyle(0x000000, 0.22);
    pg.fillEllipse(0, 4, cw * 0.95, 12);

    // Dome body (upper half ellipse, silver).
    fillHalfEllipse(pg, 0, 0, rx, ch, 0xe5e7eb, 1);
    strokeHalfEllipse(pg, 0, 0, rx, ch, 4, 0x6b7280, 1);

    // Subtle inner gradient (slightly darker dome at the bottom).
    fillHalfEllipse(pg, 0, 0, rx * 0.94, ch * 0.94, 0xd1d5db, 0.3);

    // Bright highlight streak on the upper-left (specular).
    fillHalfEllipse(pg, -rx * 0.34, -ch * 0.18, rx * 0.22, ch * 0.55, 0xffffff, 0.55);
    pg.fillStyle(0xffffff, 0.85);
    pg.fillEllipse(-rx * 0.16, -ch * 0.62, rx * 0.18, 10);

    // Right-side soft shadow.
    fillHalfEllipse(pg, rx * 0.42, -ch * 0.12, rx * 0.18, ch * 0.5, 0x4b5563, 0.18);

    // Decorative gold band wrapping the dome.
    pg.lineStyle(3, 0xfbbf24, 0.9);
    pg.strokeEllipse(0, -ch * 0.2, cw * 0.86, 16);
    pg.lineStyle(1, 0xfde68a, 0.85);
    pg.strokeEllipse(0, -ch * 0.2, cw * 0.86, 16);

    // Knob handle on top.
    const stemH = 14;
    const knobR = 13;
    const knobCY = -ch - stemH - knobR + 4;
    // Stem
    pg.fillStyle(0x6b7280, 1);
    pg.fillRect(-4, -ch - stemH + 2, 8, stemH);
    pg.lineStyle(2, 0x4b5563, 1);
    pg.strokeRect(-4, -ch - stemH + 2, 8, stemH);
    // Knob
    pg.fillStyle(0xdc2626, 1);
    pg.fillCircle(0, knobCY, knobR);
    pg.lineStyle(2, 0x7f1d1d, 1);
    pg.strokeCircle(0, knobCY, knobR);
    // Knob highlight
    pg.fillStyle(0xffffff, 0.75);
    pg.fillCircle(-4, knobCY - 4, 4);

    this.plate.add(pg);
    this._plateRestY = py;
  }

  _buildParticleLayers() {
    this.fxGraphics = this.add.graphics().setDepth(8);
    /** @type {Array<object>} */
    this._particles = [];
  }

  _buildHud() {
    const W = this.W;
    this.hudContainer = this.add.container(0, 0).setDepth(100);
    this.hudRows = {};

    const ids = Object.keys(this._playerNames);
    const n = ids.length;

    // Use 2 columns when there are more than 4 players so the scoreboard
    // doesn't eat the entire stage. Otherwise stack on the left.
    const twoCols = n > 4;
    const leftCount = twoCols ? Math.ceil(n / 2) : n;
    const innerMargin = 12;

    let pillWidth;
    if (twoCols) {
      pillWidth = (W - innerMargin * 3) / 2;       // [margin][col][margin][col][margin]
    } else {
      pillWidth = W - innerMargin * 2;
    }

    for (let i = 0; i < n; i++) {
      const id = ids[i];
      const isLeft = i < leftCount;
      const colIndex = isLeft ? i : (i - leftCount);
      const xOrigin = isLeft ? innerMargin : (innerMargin * 2 + pillWidth);
      const y = HUD_ROW_TOP + colIndex * HUD_ROW_HEIGHT;
      const row = this._buildHudRow(id, xOrigin, y, pillWidth);
      this.hudContainer.add(row.group);
      this.hudRows[id] = row;
    }
  }

  _buildHudRow(playerId, originX, originY, width) {
    const color = this._playerColors[playerId] || 0xffffff;
    const name = this._playerNames[playerId] || playerId;

    // Container origin is the top-left of the pill so width-relative
    // children can be placed in plain pill-local coords.
    const group = this.add.container(originX, originY);

    // Pill background.
    const bg = this.add.graphics();
    bg.fillStyle(0x000000, 0.42);
    bg.fillRoundedRect(0, 0, width, 36, 18);
    bg.lineStyle(3, color, 0.9);
    bg.strokeRoundedRect(0, 0, width, 36, 18);
    group.add(bg);

    // Colour tag.
    const tag = this.add.graphics();
    tag.fillStyle(color, 1);
    tag.fillRoundedRect(8, 6, 24, 24, 12);
    tag.lineStyle(2, 0xffffff, 0.9);
    tag.strokeRoundedRect(8, 6, 24, 24, 12);
    group.add(tag);

    // Player name (truncated so narrow rows still fit).
    const nameText = this.add.text(40, HUD_ROW_PILL_Y, String(name).slice(0, 8), {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '16px',
      fontStyle: 'bold',
      color: '#ffffff'
    }).setOrigin(0, 0.5);
    group.add(nameText);

    // Score dots — fit between the name and the "N/6" readout.
    const dotsLeft = 116;
    const scoreTextRight = 8;
    const dotsRight = width - scoreTextRight - 38;     // leave room for "N/6"
    const span = Math.max(0, dotsRight - dotsLeft);
    const dotStep = TARGET_SCORE > 1 ? Math.min(28, span / (TARGET_SCORE - 1)) : 28;

    const dots = [];
    for (let i = 0; i < TARGET_SCORE; i++) {
      const dot = this.add.graphics();
      dot.x = dotsLeft + i * dotStep;
      dot.y = HUD_ROW_PILL_Y;
      group.add(dot);
      dots.push(dot);
    }

    // "3 / 6" score readout (right-aligned inside the pill).
    const scoreText = this.add.text(width - scoreTextRight, HUD_ROW_PILL_Y, '0/' + TARGET_SCORE, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '16px',
      fontStyle: 'bold',
      color: '#ffffff'
    }).setOrigin(1, 0.5);
    group.add(scoreText);

    return {
      group, dots, scoreText, color, score: 0,
      // Cached so _playerHudPosition can compute world coords cheaply.
      dotsLeft, dotStep
    };
  }

  _renderHudScores(scores) {
    if (!this.hudRows) return;
    const map = {};
    for (const r of scores) map[r.id] = r.score;

    for (const id of Object.keys(this.hudRows)) {
      const row = this.hudRows[id];
      const score = map[id] || 0;

      if (score !== row.score) {
        const delta = score - row.score;
        row.score = score;
        row.scoreText.setText(score + '/' + TARGET_SCORE);
        this._pulseHudRow(row, delta >= 0);
      }

      for (let i = 0; i < TARGET_SCORE; i++) {
        const d = row.dots[i];
        d.clear();
        if (i < score) {
          d.fillStyle(row.color, 1);
          d.fillCircle(0, 0, 11);
          d.lineStyle(2, 0xffffff, 0.9);
          d.strokeCircle(0, 0, 11);
          d.fillStyle(0xffffff, 0.75);
          d.fillCircle(-3, -3, 3);
        } else {
          d.fillStyle(0xffffff, 0.15);
          d.fillCircle(0, 0, 10);
          d.lineStyle(2, 0xffffff, 0.35);
          d.strokeCircle(0, 0, 10);
        }
      }
    }
  }

  _pulseHudRow(row, positive) {
    this.tweens.killTweensOf(row.group);
    this.tweens.add({
      targets: row.group,
      scale: { from: positive ? 1.1 : 0.92, to: 1 },
      duration: 260,
      ease: 'Back.out'
    });
  }

  _buildOverlayText() {
    const W = this.W, H = this.H;

    this.statusText = this.add.text(W / 2, H * 0.34, '', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '34px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#7c2d12',
      strokeThickness: 6,
      align: 'center'
    }).setOrigin(0.5).setDepth(200);

    this.countdownText = this.add.text(W / 2, H * 0.44, '', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '170px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#dc2626',
      strokeThickness: 12
    }).setOrigin(0.5).setDepth(300);

    this.instructionsText = this.add.text(W / 2, H - 18,
      'TAP THE SCREEN WHEN THE PLATE LIFTS!', {
      fontFamily: 'system-ui, sans-serif',
      fontSize: '14px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5, 1).setDepth(200);
  }

  /** Rebuild HUD rows and status text with the new viewport dimensions.
   * Called by the SDK base class on scale.resize events. Leaves the
   * table / plate / particles untouched — their positions are computed
   * in _renderFrame() each tick from this.W/this.H, so updating the
   * cached dims is enough for them to reflow next frame. */
  onResize(width, height) {
    if (!width || !height) return;
    if (width === this._lastResizeW && height === this._lastResizeH) return;
    this._lastResizeW = width;
    this._lastResizeH = height;
    this.W = width;
    this.H = height;
    // Destroy and rebuild the HUD container so pill widths / positions
    // match the new viewport.
    if (this.hudContainer && this.hudContainer.destroy) {
      try { this.hudContainer.destroy(true); } catch (_) { /* ignore */ }
    }
    this.hudContainer = null;
    this.hudRows = {};
    try {
      this._buildHud();
      this._renderHudScores(this.lb.getAll());
    } catch (err) {
      if (typeof console !== 'undefined') console.error('[fruit-grab onResize]', err);
    }
  }

  // ─────────────────────────────────────────── per-frame rendering

  update(time, delta) {
    // Clients smooth the phase timer between incoming snapshots so
    // animations don't wait for the next host message to progress.
    if (!this.sdk.isHost && !this.state.matchOver) {
      this.state.phaseAt += delta;
    }
    this._renderFrame(delta / 1000);
    this._updateParticles(delta / 1000);
  }

  _renderFrame(_dt) {
    const phase = this.state.phase;
    const t = clamp01(this.state.phaseAt / Math.max(1, this.state.phaseLen));

    // Plate vertical offset driven by the current phase.
    let plateOffset = 0;
    if (phase === P_WAIT) {
      plateOffset = Math.sin(this.state.phaseAt * 0.018) * 1.5;
    } else if (phase === P_LIFT) {
      plateOffset = -PLATE_LIFT_PX * easeOutBack(t);
    } else if (phase === P_REVEAL) {
      plateOffset = -PLATE_LIFT_PX + Math.sin(this.state.phaseAt * 0.012) * 3;
    } else if (phase === P_GRABBED) {
      if (t < 0.6) plateOffset = -PLATE_LIFT_PX;
      else plateOffset = -PLATE_LIFT_PX * (1 - easeInQuad((t - 0.6) / 0.4));
    } else if (phase === P_MISSED) {
      plateOffset = -PLATE_LIFT_PX * (1 - easeInQuad(t));
    }
    this.plate.y = this._plateRestY + plateOffset;

    // Plate spin on grab for a little flourish.
    if (phase === P_GRABBED) {
      this.plate.rotation = Math.sin(this.state.phaseAt * 0.02) * 0.06;
    } else if (phase === P_MISSED) {
      this.plate.rotation = Math.sin(this.state.phaseAt * 0.02) * 0.04;
    } else {
      this.plate.rotation = 0;
    }

    this._renderFruit(phase);
    this._renderCountdown(phase);
    this._renderStatusText(phase);
  }

  _renderFruit(phase) {
    const fruit = this.state.fruit;
    const show = fruit && (phase === P_LIFT || phase === P_REVEAL || phase === P_GRABBED);

    if (!show) {
      this.fruitText.setVisible(false);
      this.fruitHalo.clear();
      return;
    }

    const W = this.W;
    const px = W * PLATE_X;
    const py = this._fruitRestY;

    this.fruitText.setText(fruit.emoji);
    this.fruitText.setVisible(true);
    this.fruitText.x = px;
    this.fruitText.y = py;
    this.fruitText.alpha = 1;

    let scale = 1;
    if (phase === P_LIFT) {
      // Scale up in sync with the plate lifting.
      scale = 0.1 + easeOutBack(clamp01(this.state.phaseAt / this.state.phaseLen)) * 0.9;
      this.fruitText.rotation = fruit.rotation * (1 - clamp01(this.state.phaseAt / this.state.phaseLen));
    } else if (phase === P_REVEAL) {
      const pop = clamp01(this.state.phaseAt / 260);
      scale = 1 + Math.sin(pop * Math.PI) * 0.25;
      scale += Math.sin(this.state.phaseAt * 0.008) * 0.04;
      this.fruitText.rotation = fruit.rotation + Math.sin(this.state.phaseAt * 0.01) * 0.08;
    } else if (phase === P_GRABBED) {
      const t = clamp01(this.state.phaseAt / this.state.phaseLen);
      scale = 1 + Math.sin(t * Math.PI) * 0.35;
      this.fruitText.alpha = 1 - Math.pow(t, 2);
      const winnerHud = this._playerHudPosition(this.state.winnerId);
      if (winnerHud) {
        const e = easeInCubic(t);
        this.fruitText.x = px + (winnerHud.x - px) * e;
        this.fruitText.y = py + (winnerHud.y - py) * e;
      }
      this.fruitText.rotation = fruit.rotation + t * (fruit.good ? 6 : -6);
    }
    this.fruitText.setScale(scale);

    // Halo behind the fruit — only meaningful when it's visible outside the plate.
    this.fruitHalo.clear();
    if (phase === P_REVEAL) {
      const pulse = 1 + Math.sin(this.state.phaseAt * 0.012) * 0.18;
      this.fruitHalo.fillStyle(fruit.color, 0.45);
      this.fruitHalo.fillCircle(px, py, 80 * pulse);
      this.fruitHalo.fillStyle(0xffffff, 0.32);
      this.fruitHalo.fillCircle(px, py, 50 * pulse);
    } else if (phase === P_LIFT) {
      const t = clamp01(this.state.phaseAt / this.state.phaseLen);
      this.fruitHalo.fillStyle(fruit.color, 0.35 * t);
      this.fruitHalo.fillCircle(px, py, 70 * t);
    }
  }

  _playerHudPosition(playerId) {
    const row = playerId ? this.hudRows[playerId] : null;
    if (!row) return null;
    // Fly toward the dot that just got filled (or the first slot if 0).
    const idx = Math.max(0, Math.min(TARGET_SCORE - 1, row.score - 1));
    const dotX = row.group.x + row.dotsLeft + idx * row.dotStep;
    const dotY = row.group.y + HUD_ROW_PILL_Y;
    return { x: dotX, y: dotY };
  }

  _renderCountdown(phase) {
    if (phase !== P_COUNTDOWN) {
      if (this.countdownText.visible) this.countdownText.setVisible(false);
      return;
    }
    const remainingMs = Math.max(0, this.state.phaseLen - this.state.phaseAt);
    const remaining = Math.ceil(remainingMs / 1000);
    const txt = remaining > 0 ? String(remaining) : 'GO!';
    if (this.countdownText.text !== txt) {
      this.countdownText.setText(txt);
      this.tweens.killTweensOf(this.countdownText);
      this.countdownText.setScale(1.5);
      this.countdownText.alpha = 0;
      this.tweens.add({
        targets: this.countdownText,
        scale: 1,
        alpha: 1,
        duration: 300,
        ease: 'Cubic.out'
      });
      this._playSfx('fg_tick');
    }
    this.countdownText.setVisible(true);
  }

  _renderStatusText(phase) {
    let msg = '';
    const winnerName = this.state.winnerId
      ? (this._playerNames[this.state.winnerId] || this.state.winnerId)
      : '';

    if (phase === P_COUNTDOWN) msg = 'GET READY!';
    else if (phase === P_WAIT) msg = "what's under the plate?";
    else if (phase === P_LIFT) msg = '👀';
    else if (phase === P_REVEAL) {
      msg = this.state.fruit && this.state.fruit.good ? 'GRAB IT!' : "DON'T GRAB!";
    }
    else if (phase === P_GRABBED) {
      if (this.state.fruit && this.state.fruit.good) msg = winnerName + ' — YUMMY!';
      else msg = winnerName + ' — YUCK!';
    }
    else if (phase === P_MISSED) msg = 'nobody grabbed it…';
    else if (phase === P_END) {
      const wn = this.state.finalWinnerId
        ? (this._playerNames[this.state.finalWinnerId] || this.state.finalWinnerId)
        : '';
      msg = wn + ' WINS! 🏆';
    }

    if (this.statusText.text !== msg) {
      this.statusText.setText(msg);
      if (msg) {
        this.tweens.killTweensOf(this.statusText);
        this.statusText.setScale(1.4);
        this.tweens.add({
          targets: this.statusText,
          scale: 1,
          duration: 280,
          ease: 'Back.out'
        });
      }
    }
  }

  // ─────────────────────────────────────────────────── particles

  _spawnRevealBurst(fruit) {
    if (!fruit) return;
    const W = this.W;
    const cx = W * PLATE_X;
    const cy = this._fruitRestY;
    const color = fruit.color;
    // Main ring of fruit-coloured sparks.
    for (let i = 0; i < 24; i++) {
      const a = (i / 24) * Math.PI * 2;
      const sp = 180 + Math.random() * 140;
      this._particles.push({
        x: cx, y: cy,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp * 0.7 - 60,
        color, shape: 'circle',
        size: 5 + Math.random() * 3,
        life: 0, ttl: 0.7,
        rot: 0, vrot: 0, gravity: 260
      });
    }
    // White sparkles on top.
    for (let i = 0; i < 18; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 50 + Math.random() * 120;
      this._particles.push({
        x: cx + Math.cos(a) * 20, y: cy + Math.sin(a) * 20,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp - 30,
        color: 0xffffff, shape: 'sparkle',
        size: 3, life: 0, ttl: 0.5,
        rot: 0, vrot: 0, gravity: 0
      });
    }
  }

  _spawnConfetti() {
    const W = this.W;
    const cx = W * PLATE_X;
    const cy = this._fruitRestY - 20;
    const palette = [0xef4444, 0xfb923c, 0xfacc15, 0x16a34a, 0x3b82f6, 0x8b5cf6, 0xec4899];
    for (let i = 0; i < 60; i++) {
      const a = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI;
      const sp = 250 + Math.random() * 260;
      this._particles.push({
        x: cx + (Math.random() - 0.5) * 60,
        y: cy,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp - 80,
        color: palette[Math.floor(Math.random() * palette.length)],
        shape: 'rect',
        size: 6 + Math.random() * 5,
        life: 0, ttl: 1.6,
        rot: Math.random() * Math.PI * 2,
        vrot: (Math.random() - 0.5) * 10,
        gravity: 520
      });
    }
  }

  _spawnPoof() {
    const W = this.W;
    const cx = W * PLATE_X;
    const cy = this._fruitRestY;
    for (let i = 0; i < 22; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 70 + Math.random() * 80;
      this._particles.push({
        x: cx + (Math.random() - 0.5) * 30,
        y: cy + (Math.random() - 0.5) * 20,
        vx: Math.cos(a) * sp,
        vy: Math.sin(a) * sp - 40,
        color: 0x57534e,
        shape: 'cloud',
        size: 14 + Math.random() * 10,
        life: 0, ttl: 1.0,
        rot: 0, vrot: 0, gravity: -40
      });
    }
  }

  _spawnFireworks() {
    const W = this.W, H = this.H;
    const colors = [0xef4444, 0xfacc15, 0x3b82f6, 0x10b981, 0xec4899, 0x8b5cf6];
    for (let b = 0; b < 4; b++) {
      const cx = W * (0.15 + Math.random() * 0.7);
      const cy = H * (0.2 + Math.random() * 0.25);
      const color = colors[b % colors.length];
      this.time.delayedCall(b * 350, () => {
        for (let i = 0; i < 42; i++) {
          const a = (i / 42) * Math.PI * 2;
          const sp = 210 + Math.random() * 120;
          this._particles.push({
            x: cx, y: cy,
            vx: Math.cos(a) * sp, vy: Math.sin(a) * sp,
            color, shape: 'circle',
            size: 5, life: 0, ttl: 1.3,
            rot: 0, vrot: 0, gravity: 180
          });
        }
        this._playSfx('fg_tick');
      });
    }
  }

  _updateParticles(dt) {
    const g = this.fxGraphics;
    g.clear();
    for (let i = this._particles.length - 1; i >= 0; i--) {
      const p = this._particles[i];
      p.life += dt;
      if (p.life >= p.ttl) { this._particles.splice(i, 1); continue; }
      p.vy += p.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.vrot * dt;
      const a = 1 - (p.life / p.ttl);

      if (p.shape === 'circle') {
        g.fillStyle(p.color, a);
        g.fillCircle(p.x, p.y, p.size * (0.3 + a * 0.7));
      } else if (p.shape === 'rect') {
        g.fillStyle(p.color, a);
        g.fillRect(p.x - p.size / 2, p.y - p.size / 3, p.size, p.size * 0.6);
      } else if (p.shape === 'sparkle') {
        g.fillStyle(0xffffff, a);
        g.fillCircle(p.x, p.y, p.size * a);
        g.lineStyle(2 * a, 0xffffff, a * 0.8);
        g.strokeCircle(p.x, p.y, p.size * 2 + (1 - a) * 6);
      } else if (p.shape === 'cloud') {
        g.fillStyle(p.color, a * 0.55);
        g.fillCircle(p.x, p.y, p.size * (1 + p.life * 0.8));
      }
    }
  }

  // ─────────────────────────────────────────────────── local input

  _setupLocalInput() {
    // Whole-screen tap = grab. Sends a one-shot 'fg-grab' event over the
    // SDK bus instead of latching the virtual fire button — single-shot
    // semantics, no per-tick serialization timing to worry about.
    const onGrabPress = (p) => {
      this._fireLocalGrab();
      if (p) this._spawnTapRipple(p.x, p.y);
    };

    this.input.on('pointerdown', onGrabPress);

    // Keyboard fallback (Space) — convert keypress edge into a grab.
    if (this.input.keyboard) {
      this.input.keyboard.on('keydown-SPACE', () => this._fireLocalGrab());
    }
  }

  _fireLocalGrab() {
    const playerId = this.sdk.localPlayerId;
    const payload = { playerId, ts: Date.now() };
    if (this.sdk.isHost) {
      // Host: push directly to its own queue. No round-trip.
      this._grabQueue.push(payload);
    } else {
      // Client: tell the host. The host's `sdk.on('fg-grab', ...)`
      // listener will queue it.
      this.sdk.emit('fg-grab', payload);
    }
  }

  _spawnTapRipple(x, y) {
    const ring = this.add.graphics().setDepth(7);
    ring.lineStyle(4, 0xffffff, 0.9);
    ring.strokeCircle(0, 0, 22);
    ring.x = x;
    ring.y = y;
    this.tweens.add({
      targets: ring,
      scaleX: 2.2,
      scaleY: 2.2,
      alpha: 0,
      duration: 420,
      ease: 'Cubic.out',
      onComplete: () => ring.destroy()
    });
  }

  // ─────────────────────────────────────────────────── music

  _startMusic() {
    if (this.sdk.audio && this.sdk.audio.playMusic) {
      try { this.sdk.audio.playMusic('fg_music'); } catch (_) { /* ignore */ }
    }
  }

  _teardown() {
    try { if (this.sdk.audio && this.sdk.audio.stopMusic) this.sdk.audio.stopMusic(); } catch (_) {}
    try { if (this.lb && this.lb.destroy) this.lb.destroy(); } catch (_) {}
    try { if (this._unsubGrab) this._unsubGrab(); } catch (_) {}
    try { if (this._unsubWrong) this._unsubWrong(); } catch (_) {}
  }
}

// ───────────────────────────────────────────────────────── helpers

function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

function easeOutBack(x) {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  const t = x - 1;
  return 1 + c3 * t * t * t + c1 * t * t;
}

function easeInQuad(x) { return x * x; }

function easeInCubic(x) { return x * x * x; }

/**
 * Fill the upper half of an ellipse centered at (cx, cy), with horizontal
 * radius rx and total upward extent ry. The "rim" line is at y = cy.
 */
function fillHalfEllipse(g, cx, cy, rx, ry, color, alpha) {
  g.fillStyle(color, alpha);
  g.beginPath();
  g.moveTo(cx + rx, cy);
  const steps = 36;
  for (let i = 1; i <= steps; i++) {
    const a = (i / steps) * Math.PI;            // 0 → π
    const x = cx + rx * Math.cos(a);
    const y = cy - ry * Math.sin(a);
    g.lineTo(x, y);
  }
  g.lineTo(cx + rx, cy);
  g.closePath();
  g.fillPath();
}

function strokeHalfEllipse(g, cx, cy, rx, ry, lineWidth, color, alpha) {
  g.lineStyle(lineWidth, color, alpha);
  g.beginPath();
  g.moveTo(cx + rx, cy);
  const steps = 36;
  for (let i = 1; i <= steps; i++) {
    const a = (i / steps) * Math.PI;
    const x = cx + rx * Math.cos(a);
    const y = cy - ry * Math.sin(a);
    g.lineTo(x, y);
  }
  g.strokePath();
}
